import { z } from 'zod';
export * from './cpf.js';
/* ============================================================================
 * Channel taxonomy
 *
 * Every channel this hub is meant to absorb has a slot here from day one, even
 * though Phase 2 only implements `internal`. Adding a channel later must never
 * mean touching the messaging engine — only registering an adapter.
 * ==========================================================================*/
export const CHANNEL_TYPES = [
    'internal', // dev customer simulator (Phase 2)
    'whatsapp', // Meta WhatsApp Cloud API
    'whatsapp_qr', // unofficial QR-session bridge
    'instagram', // Instagram Direct via Meta Messenger API
    'mercadolivre', // ML post-sale messages
    'mercadolivre_question', // ML pre-sale questions (different policy envelope)
    'shopee', // Shopee Open Platform chat
    'amazon', // Amazon Buyer-Seller Messaging (SP-API)
    'tiktok', // TikTok Shop customer service
    'tiktok_comment', // TikTok post comments
    'site', // first-party website chat
    'email',
];
export const channelTypeSchema = z.enum(CHANNEL_TYPES);
/**
 * The hard cap each channel puts on one outgoing text, in characters.
 *
 * One number, several readers: the adapter that refuses the send, the compliance check that
 * blocks a suggestion, and the composer that counts down while an agent types. They used to be
 * separate copies, with a comment claiming they were not — and a copy that drifts is worse than
 * no limit, because the composer would happily accept text the adapter then rejects.
 *
 * These are the marketplace's caps, not ours, so they belong here rather than behind the
 * adapter registry: an unconfigured channel falls back to the simulator, and asking the
 * simulator how long a Mercado Livre answer may be gives a confident wrong answer.
 */
export const CHANNEL_MAX_TEXT = {
    mercadolivre_question: 2000,
    /** ML reports this as `seller_max_message_length`; 350 confirmed live. */
    mercadolivre: 350,
    /** Exceeding it returns `message_too_long`. */
    shopee: 600,
    whatsapp: 4096,
    whatsapp_qr: 4096,
    internal: 4000,
};
/** The cap for a channel, or null where none is known. */
export function maxTextFor(channelType) {
    return CHANNEL_MAX_TEXT[channelType] ?? null;
}
/** The first hour of the working day — when a morning follow-up should actually land. */
export const FOLLOW_UP_FIRST_HOUR = 9;
export function followUpAt(now, key) {
    switch (key) {
        case '1h':
            return new Date(now.getTime() + 3_600_000);
        case '2h':
            return new Date(now.getTime() + 2 * 3_600_000);
        case 'tomorrow_first': {
            // Built from the calendar date, not from a 24-hour offset: adding a day to the DATE and
            // then setting the hour is what keeps this at 09:00 even across a clock change.
            const d = new Date(now);
            d.setDate(d.getDate() + 1);
            d.setHours(FOLLOW_UP_FIRST_HOUR, 0, 0, 0);
            return d;
        }
        case 'tomorrow_same': {
            const d = new Date(now);
            d.setDate(d.getDate() + 1);
            return d;
        }
    }
}
/** Channels whose conversations are public (a question board, a comment thread). */
export const PUBLIC_CHANNELS = ['mercadolivre_question', 'tiktok_comment'];
/**
 * The channel types a single inbox tab stands for.
 *
 * Several tabs cover more than one channel type, because the split is an INGEST detail the agent
 * has no reason to care about: a Mercado Livre pre-sale question and a post-sale message are one
 * marketplace to the person answering them, and WhatsApp arrives through either the Cloud API or
 * the QR bridge depending on which one is linked.
 *
 * This lived only in the tabs' "is it connected" check, while counting and filtering compared the
 * channel type directly — so the Mercado Livre tab hid all 616 questions behind the 23 post-sale
 * threads, and the WhatsApp tab was empty because every conversation was `whatsapp_qr`.
 */
export const CHANNEL_TAB_MEMBERS = {
    whatsapp: ['whatsapp', 'whatsapp_qr'],
    mercadolivre: ['mercadolivre', 'mercadolivre_question'],
    tiktok: ['tiktok', 'tiktok_comment'],
};
/** Every channel type a tab covers — itself, when it covers nothing else. */
export function channelTabMembers(tab) {
    return CHANNEL_TAB_MEMBERS[tab] ?? [tab];
}
/** Does this conversation belong under that tab? */
export function channelMatchesTab(tab, channelType) {
    return tab === 'todos' || channelTabMembers(tab).includes(channelType);
}
/**
 * Which id space a channel's user ids live in.
 *
 * Several channel types are separate *conversation surfaces* over one *account system*. A
 * Mercado Livre buyer has one user id whether they ask a pre-sale question or message about an
 * order; keying identities by channel type would file the same human twice and then ask an agent
 * to "confirm" they are the same person — a question with only one possible answer.
 *
 * Identity rows are therefore keyed by namespace, not channel type.
 */
export function identityNamespace(channelType) {
    switch (channelType) {
        case 'mercadolivre':
        case 'mercadolivre_question':
            return 'mercadolivre';
        case 'tiktok':
        case 'tiktok_comment':
            return 'tiktok';
        // Same phone number, whether it reaches us via Cloud API or the QR bridge.
        case 'whatsapp':
        case 'whatsapp_qr':
            return 'whatsapp';
        default:
            return channelType;
    }
}
/**
 * Which legal entity (CNPJ) a sales channel trades under.
 *
 * Deliberately NOT derived from the store: a storefront and a company are different things here.
 * One Mercado Livre account lives on the Quartinhos store but bills under Pixly, so inferring
 * the entity from the store would label it wrong — and this is the field agents use to know
 * which company a customer actually bought from.
 */
export const BUSINESSES = ['adesivar', 'kids', 'pixly'];
export const businessSchema = z.enum(BUSINESSES);
/** Ring colour drawn around a channel icon. Null business = no ring. */
export const BUSINESS_META = {
    adesivar: { label: 'Adesivar', color: '#e5484d' },
    kids: { label: 'Kids', color: '#2f6fed' },
    pixly: { label: 'Pixly', color: '#eab308' },
};
/* ============================================================================
 * Enums mirrored by the database
 * ==========================================================================*/
export const CONVERSATION_STATUSES = [
    'new',
    'open',
    'waiting_customer',
    'waiting_internal',
    'resolved',
];
export const conversationStatusSchema = z.enum(CONVERSATION_STATUSES);
export const PRIORITIES = ['low', 'normal', 'high'];
export const MESSAGE_DIRECTIONS = ['incoming', 'outgoing'];
export const SENDER_TYPES = ['customer', 'human', 'ai', 'system'];
export const MESSAGE_TYPES = [
    'text',
    'image',
    'audio',
    'video',
    'document',
    /**
     * Figurinha — o "emoji" da Shopee, que vem de um pacote dela e não é emoji Unicode.
     *
     * Tipo próprio e não `image`: uma figurinha é uma reação, do tamanho de um emoji, e tratá-la
     * como foto faz duas coisas erradas ao mesmo tempo — ocupa a bolha inteira e conta como algo
     * que o cliente mandou para ser respondido.
     */
    'sticker',
    'product',
    'order',
    'internal_note',
    'system_event',
];
/** The hard boundary. `internal` never leaves the building. */
export const VISIBILITIES = ['customer', 'internal'];
export const MESSAGE_STATUSES = ['pending', 'sent', 'delivered', 'read', 'failed'];
export const DELIVERY_MODES = ['freeform', 'template'];
export const USER_ROLES = ['admin', 'supervisor', 'agent'];
/**
 * Ticket lifecycle. `waiting_customer` exists because the most common reason a ticket stalls is
 * that we are waiting on the customer, and without a name for it those tickets look identical to
 * ones nobody has picked up — the queue stops meaning anything.
 */
export const TICKET_STATUSES = ['open', 'in_progress', 'waiting_customer', 'resolved'];
export const TICKET_PRIORITIES = ['low', 'normal', 'high', 'urgent'];
export const TICKET_STATUS_LABELS = {
    open: 'Aberto',
    in_progress: 'Em andamento',
    waiting_customer: 'Aguardando cliente',
    resolved: 'Resolvido',
};
export const TICKET_PRIORITY_LABELS = {
    low: 'Baixa',
    normal: 'Normal',
    high: 'Alta',
    urgent: 'Urgente',
};
export const CONNECTION_STATUSES = [
    'simulation',
    'disconnected',
    'connected',
    'error',
];
/* ============================================================================
 * Realtime event envelope
 * ==========================================================================*/
export const REALTIME_EVENT_TYPES = [
    'message.created',
    'message.status_changed',
    'conversation.created',
    'conversation.updated',
    'conversation.assigned',
    'conversation.status_changed',
    'conversation.reopened',
    'conversation.unread_changed',
    'note.created',
    'ticket.created',
    'ticket.updated',
    'identity.linked',
    'ping',
];
/**
 * The four colours an order status can wear. Exported as a value, not just a type, because the
 * providers' own status strings are mapped onto it in the API and rendered as `.pill.<tone>` in
 * the web CSS — three places that silently disagreed until 2026-08-18, when ~30,600 of 39,200
 * orders were rendering with no colour at all because the syncs emitted 'ok'/'warn' and only
 * 'sent' happened to overlap. Anything that produces a tone must be typed against this.
 */
export const ORDER_STATUS_TONES = ['sent', 'deliv', 'proc', 'canc'];
export const channelAutoReplyPatchSchema = z.object({
    enabled: z.boolean(),
    minCertainty: z.number().int().min(0).max(100),
});
/**
 * De onde veio o texto.
 *
 * `importada` não é um detalhe técnico: 1.763 das 2.164 respostas em produção foram escritas no
 * app do canal e trazidas para cá pelo poller. Um registro que as escondesse mostraria um quinto
 * do que a empresa responde.
 *
 * `automatica` é separada de `ia` porque são coisas diferentes: em `ia` uma pessoa leu e apertou
 * enviar; em `automatica` ninguém leu. Juntá-las apagaria do registro exatamente o que um admin
 * abre o registro para conferir.
 */
export const LOG_ORIGINS = ['automatica', 'ia', 'humana', 'importada'];
export const logQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(3650).optional(),
    channel: channelTypeSchema.optional(),
    /** Id de quem escreveu, ou `fora` para o que veio pronto do canal. */
    author: z.string().optional(),
    origin: z.enum(LOG_ORIGINS).optional(),
    /** Busca no texto da resposta E no da pergunta — procurar só na resposta perde o assunto. */
    q: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional().default(50),
    offset: z.coerce.number().int().min(0).optional().default(0),
});
export const quickReplySchema = z.object({
    title: z.string().trim().min(1).max(80),
    body: z.string().trim().min(1).max(4000),
});
/* ============================================================================
 * Request schemas
 * ==========================================================================*/
export const loginSchema = z.object({
    email: z.string().email(),
    password: z.string().min(1),
});
/** Minimum length for a new password. Long-but-simple beats short-but-gnarly for a team tool. */
export const MIN_PASSWORD_LENGTH = 12;
export const changePasswordSchema = z
    .object({
    currentPassword: z.string().min(1, 'Informe a senha atual.'),
    newPassword: z
        .string()
        .min(MIN_PASSWORD_LENGTH, `A nova senha precisa ter pelo menos ${MIN_PASSWORD_LENGTH} caracteres.`)
        .max(200, 'A nova senha é longa demais.'),
})
    .refine((d) => d.currentPassword !== d.newPassword, {
    message: 'A nova senha precisa ser diferente da atual.',
    path: ['newPassword'],
});
export const listConversationsQuerySchema = z.object({
    channelType: z.string().optional(),
    storeIds: z.string().optional(), // csv
    channelAccountIds: z.string().optional(), // csv
    status: z
        .enum(['all', 'open', 'unassigned', 'waiting', 'resolved', 'unread'])
        .optional()
        .default('all'),
    assignedAgentId: z.string().uuid().optional(),
    priority: z.enum(PRIORITIES).optional(),
    slaBreached: z.coerce.boolean().optional(),
    q: z.string().optional(),
    limit: z.coerce.number().min(1).max(200).optional().default(80),
});
export const sendMessageSchema = z.object({
    text: z.string().max(8000).optional(),
    clientMessageId: z.string().max(80).optional(),
    productIds: z.array(z.string().uuid()).max(10).optional(),
    deliveryMode: z.enum(DELIVERY_MODES).optional().default('freeform'),
    /**
     * A sugestão da IA foi enviada exatamente como veio.
     *
     * Marcado na mensagem, e não deduzido depois: `suggestion_feedback` registra o desfecho por
     * CONVERSA e não guarda o id da mensagem, então casar as duas coisas seria adivinhação por
     * texto. E o autor continua sendo a pessoa que apertou enviar — isto diz quem ESCREVEU, não
     * quem respondeu por isso.
     */
    aiVerbatim: z.boolean().optional(),
    /**
     * Imagens anexadas, cada uma como data URI.
     *
     * Data URI e não upload separado porque é assim que as imagens RECEBIDAS já são guardadas —
     * não há object storage neste deploy, e montar um seria infraestrutura inteira para uma foto
     * de parede. O teto de 7 MB é o data URI (base64 infla ~33%), o que dá pouco mais de 5 MB de
     * imagem real, que é o que os adaptadores aceitam.
     */
    images: z
        .array(z.object({
        dataUrl: z.string().regex(/^data:image\/(png|jpe?g|webp|gif);base64,[A-Za-z0-9+/=]+$/).max(7_000_000),
        filename: z.string().max(120).optional(),
    }))
        .max(5)
        .optional(),
    /**
     * A avaliação de cordialidade que este envio fecha. A escolha (sugestão, editada, original) é
     * deduzida no servidor comparando o texto enviado com o que foi avaliado e sugerido.
     */
    cordialityReviewId: z.string().uuid().optional(),
});
export const internalNoteSchema = z.object({
    text: z.string().min(1).max(8000),
    clientMessageId: z.string().max(80).optional(),
});
export const patchConversationSchema = z.object({
    status: conversationStatusSchema.optional(),
    assignedAgentId: z.string().uuid().nullable().optional(),
    priority: z.enum(PRIORITIES).optional(),
});
export const simSendSchema = z.object({
    conversationId: z.string().uuid(),
    text: z.string().min(1).max(4000),
    externalEventId: z.string().max(200).optional(),
});
export const devCustomerMessageSchema = z.object({
    customerId: z.string().uuid().optional(),
    conversationId: z.string().uuid(),
    text: z.string().min(1).max(4000),
    externalEventId: z.string().max(200).optional(),
});
/* ============================================================================
 * Avatar colours
 * ==========================================================================*/
/**
 * Background palette for customer avatars.
 *
 * Mid-saturation on purpose: the Fun Emoji faces are drawn in strong colours, and a background
 * competing with them turns the inbox into confetti — the point is telling rows apart, not
 * decoration. Every entry also has to stay readable behind the small channel badge.
 */
export const AVATAR_COLORS = [
    '#5b6ef5', // indigo
    '#e8776b', // coral
    '#2f9e78', // green
    '#c78b1f', // amber
    '#a855c7', // purple
    '#0ea5b7', // teal
    '#e5484d', // red
    '#2f6fed', // blue
    '#7c5cf5', // violet
    '#d9538f', // pink
    '#5a8f2f', // olive
    '#b45309', // rust
];
/**
 * Picks a stable colour for a customer.
 *
 * Deterministic on the seed so a customer keeps the same colour forever — a colour that changes
 * between page loads is worse than no colour, because agents start recognising people by it.
 * Seed with the customer id, not the name: names get edited, and two people share one often.
 */
export function avatarColorFor(seed) {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
        hash = (hash * 31 + seed.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
export const INTEGRATION_PROVIDERS = [
    {
        id: 'woocommerce',
        label: 'WooCommerce',
        description: 'Catálogo da loja própria: produtos, categorias e imagens. Somente leitura — preços e estoque da loja nunca entram em resposta de marketplace.',
        testable: true,
        fields: [
            { key: 'baseUrl', label: 'URL da loja', secret: false, placeholder: 'https://quartinhos.com.br' },
            { key: 'consumerKey', label: 'Consumer key', secret: true, placeholder: 'ck_…' },
            { key: 'consumerSecret', label: 'Consumer secret', secret: true, placeholder: 'cs_…' },
        ],
    },
    {
        id: 'openrouter',
        label: 'OpenRouter',
        description: 'Modelo que gera as sugestões de resposta.',
        testable: true,
        fields: [
            { key: 'apiKey', label: 'API key', secret: true, placeholder: 'sk-or-v1-…' },
            {
                key: 'model',
                label: 'Modelo',
                secret: false,
                placeholder: '~deepseek/deepseek-v4-flash-latest',
                hint: 'O ~ faz parte do id do modelo. Vazio = openai/gpt-5.6-luna.',
            },
        ],
    },
    {
        id: 'alert_webhook',
        label: 'Webhook de alertas',
        description: 'Chama um agente ou automação a cada mensagem de cliente, em qualquer canal, que chega fora do horário de atendimento humano (seg–sex 8h–17h, sáb 8h–12h, horário de Brasília) ou num feriado. Envia só identificadores (conversa, mensagem, canal), sem dados pessoais.',
        testable: true,
        fields: [
            { key: 'url', label: 'URL do webhook', secret: true, placeholder: 'https://…' },
            {
                key: 'key',
                label: 'Chave',
                secret: true,
                hint: 'Vai em Authorization: Bearer <chave>. O corpo também é assinado com ela: X-SAC-Signature: sha256=<HMAC-SHA256>.',
            },
            {
                key: 'holidays',
                label: 'Feriados (sem atendimento humano)',
                secret: false,
                placeholder: '02/11/2026, 15/11/2026, …',
                hint: 'Datas DD/MM/AAAA separadas por vírgula. Nesses dias toda mensagem chama o webhook.',
            },
        ],
    },
    {
        id: 'learning_webhook',
        label: 'Webhook de aprendizado',
        description: 'Avisa o agente de aprendizado, a qualquer hora: message.received a cada mensagem de cliente (todos os canais) e suggestion.decided quando alguém envia, edita, reescreve ou descarta uma sugestão da IA. Só identificadores; o agente lê os detalhes pelo MCP (list_suggestions, get_conversation). Entrega com novas tentativas e event_id para descartar repetidos.',
        testable: true,
        fields: [
            { key: 'url', label: 'URL do webhook', secret: true, placeholder: 'https://…' },
            {
                key: 'key',
                label: 'Chave',
                secret: true,
                hint: 'Vai em Authorization: Bearer <chave>. O corpo também é assinado com ela: X-SAC-Signature: sha256=<HMAC-SHA256>.',
            },
        ],
    },
    {
        id: 'mercadolivre',
        label: 'Mercado Livre',
        description: 'Conecte uma conta autorizando o SAC no próprio Mercado Livre. Reconectar a mesma conta renova o acesso sem duplicar o canal.',
        testable: false,
        fields: [],
        oauth: { startPath: '/api/auth/ml/start', channelType: 'mercadolivre', needsStore: true },
    },
    {
        id: 'shopee',
        label: 'Shopee',
        description: 'Conecte uma loja autorizando o SAC no Seller Centre. A autorização expira periodicamente — reconectar pela mesma loja renova o acesso.',
        testable: false,
        fields: [
            {
                key: 'shopIds',
                label: 'Lojas próprias (shop_id)',
                secret: false,
                placeholder: '638318083, 396831191',
                hint: 'Links de anúncio da Shopee só passam no chat da Shopee se forem destas lojas. Vazio = Kids Decor (638318083) e Adesivar (396831191).',
            },
        ],
        oauth: { startPath: '/api/auth/shopee/start', channelType: 'shopee', needsStore: true },
    },
    {
        id: 'typesafe',
        label: 'TypeSafe (Jev)',
        description: 'Classifica o assunto de cada conversa e desenha a etiqueta na caixa de entrada. Sem esta ' +
            'chave nada muda: as conversas simplesmente não recebem etiqueta.',
        testable: true,
        fields: [{ key: 'apiKey', label: 'API key', secret: true, env: 'TYPESAFE_API_KEY' }],
    },
    {
        id: 'cordialidade',
        label: 'Cordialidade',
        description: 'Antes de enviar uma resposta escrita ou editada à mão, o Jev avalia o tom e, se soar seco, ' +
            'o Gemini sugere uma versão mais calorosa com os mesmos fatos. O atendente sempre decide. ' +
            'Nasce desligado. Usa a chave do TypeSafe e a do OpenRouter acima.',
        testable: false,
        fields: [
            { key: 'enabled', label: 'Ligado', secret: false, placeholder: 'não', hint: '"sim" liga. Qualquer outra coisa, desligado.', env: 'CORDIALITY_CHECK_ENABLED' },
            { key: 'users', label: 'Só para estes atendentes', secret: false, placeholder: 'helena@quartinhos.com.br', hint: 'E-mails separados por vírgula. Vazio = todos.' },
            { key: 'threshold', label: 'Nota mínima (0–100)', secret: false, placeholder: '60' },
            { key: 'minChars', label: 'Não avaliar abaixo de (caracteres)', secret: false, placeholder: '25' },
            { key: 'skipCourtesy', label: 'Pular saudação, agradecimento e link solto', secret: false, placeholder: 'sim' },
            { key: 'rewriteModel', label: 'Modelo da reescrita', secret: false, placeholder: '~google/gemini-flash-latest', env: 'CORDIALITY_REWRITE_MODEL' },
        ],
    },
    {
        id: 'grok',
        label: 'Grok — perguntas ao SAC',
        description: 'Para onde devolver a escolha quando alguém clica numa opção na aba Robôs. A URL e a chave ' +
            'aparecem no painel da rotina do Grok, depois que a rotina é criada. Sem isto configurado as ' +
            'perguntas ainda chegam — o robô só precisa buscá-las por conta própria.',
        testable: false,
        fields: [
            {
                key: 'callbackUrl',
                label: 'URL do webhook',
                secret: false,
                placeholder: 'https://…',
                hint: 'Precisa ser https.',
            },
            {
                key: 'headerName',
                label: 'Nome do cabeçalho',
                secret: false,
                placeholder: 'Authorization',
                hint: 'Copie do painel da rotina — nem sempre é Authorization.',
            },
            { key: 'headerValue', label: 'Chave do remetente', secret: true },
        ],
    },
];
export function integrationProvider(id) {
    return INTEGRATION_PROVIDERS.find((p) => p.id === id);
}
export function integrationField(providerId, key) {
    return integrationProvider(providerId)?.fields.find((f) => f.key === key);
}
export * from './whatsappMarkup.js';
/* ============================================================================
 * Perguntas do robô
 *
 * Um robô externo pergunta, alguém do SAC clica numa opção, a escolha volta para o robô. Os
 * limites moram aqui porque três lugares precisam do MESMO número: o servidor MCP que valida o
 * que o robô manda, a rota que valida o clique, e a tela que desenha os botões.
 *
 * Os limites não são estéticos. `question` e `label` são texto de TERCEIRO — chegam por HTTP,
 * de fora — e vão ser renderizados numa tela onde alguém autoriza uma ação irreversível. Seis
 * opções é o que cabe numa tela sem virar rolagem, e uma pergunta que não cabe em 500
 * caracteres não é uma pergunta de múltipla escolha.
 * ==========================================================================*/
export const BOT_QUESTION_MAX = 500;
export const BOT_OPTION_LABEL_MAX = 80;
export const BOT_OPTIONS_MIN = 2;
export const BOT_OPTIONS_MAX = 6;
/** `id` é contrato com o robô — estável, sem acento, sem espaço. `label` é o que a pessoa lê. */
export const botOptionSchema = z.object({
    id: z.string().regex(/^[a-z0-9_-]{1,40}$/, 'id deve ser [a-z0-9_-], até 40 caracteres'),
    label: z.string().min(1).max(BOT_OPTION_LABEL_MAX),
});
export const botOptionsSchema = z
    .array(botOptionSchema)
    .min(BOT_OPTIONS_MIN)
    .max(BOT_OPTIONS_MAX)
    // Dois ids iguais tornariam a escolha ambígua no lado do robô, que decide pelo id.
    .refine((o) => new Set(o.map((x) => x.id)).size === o.length, 'ids das opções precisam ser únicos');
export const askBotQuestionSchema = z.object({
    bot: z.string().regex(/^[a-z0-9_-]{1,60}$/, 'bot deve ser [a-z0-9_-], até 60 caracteres'),
    question: z.string().min(1).max(BOT_QUESTION_MAX),
    options: botOptionsSchema,
    context: z.record(z.unknown()).optional(),
});
export const answerBotQuestionSchema = z.object({ optionId: z.string().max(40) });
/* ============================================================================
 * Assunto da conversa — a etiqueta que aparece ao lado do ícone do canal
 *
 * Seis assuntos e mais nada. A lista é curta de propósito: a etiqueta serve para varrer a caixa
 * de entrada com o olho e decidir o que pegar primeiro, e um vocabulário que não cabe num relance
 * não faz isso. "Nenhum" não é um sexto assunto — é a ausência de etiqueta, e é o resultado certo
 * na maioria das conversas.
 *
 * Mora em contracts porque três lugares precisam da MESMA definição: o classificador manda os
 * critérios para o modelo, a tela desenha o emoji, e o teste confere que os dois falam do mesmo.
 * Mudar o texto de um critério muda o que o modelo decide — não é rótulo de interface, é prompt.
 * ==========================================================================*/
export const CONVERSATION_TOPICS = {
    troca_devolucao: {
        emoji: '↩️',
        label: 'Troca ou devolução',
        criterio: 'Troca, devolução, arrependimento, reembolso, cancelamento ou produto com defeito',
    },
    customizado: {
        emoji: '🎨',
        label: 'Customizado ou personalizado',
        criterio: 'Quer um produto customizado ou personalizado: arte própria, medida sob encomenda, nome, cor ou tema fora do catálogo',
    },
    venda_quente: {
        emoji: '🔥',
        label: 'Dúvida de venda',
        criterio: 'Quer comprar: preço, medida, se cola na superfície, se tem em estoque, quantas unidades — e também quem só ' +
            'está olhando ("vocês têm adesivo de borboleta?", "quero ver opções de X"). Ainda é alguém querendo comprar.',
    },
    quantidade: {
        emoji: '🔢',
        label: 'Quantidade errada',
        criterio: 'Quantidade: veio menos (ou mais) do que a pessoa esperava, faltou item no pacote, ou dúvida sobre quantas unidades o pedido tem',
    },
    entrega: {
        emoji: '🚚',
        label: 'Entrega ou rastreamento',
        criterio: 'Entrega, prazo, rastreamento, transportadora, extravio, retirada ou pedido que não chegou',
    },
    problema_site: {
        emoji: '👨🏻‍💻',
        label: 'Problema no site',
        criterio: 'Problema técnico no nosso site que impede a compra: pagamento recusado, cupom que não aplica, carrinho, frete que não calcula',
    },
};
/** A chave que o classificador devolve quando nada encaixa. Nunca vira etiqueta. */
export const TOPIC_NONE = 'nenhum';
export const CONVERSATION_TOPIC_KEYS = Object.keys(CONVERSATION_TOPICS);
export function isConversationTopic(v) {
    return typeof v === 'string' && v in CONVERSATION_TOPICS;
}
/* ============================================================================
 * Arquivo perigoso — o que nunca é guardado nem oferecido para download
 *
 * Origem: uma campanha no WhatsApp, em out/2026, de números diferentes com o mesmo texto ("o
 * produto chegou danificado") e um anexo chamado de nota fiscal — `NFe…-18-09-2026.pdf.js` — que
 * é um JavaScript de ~5 MB. No Windows, dois cliques num `.js` executam o script pelo próprio
 * sistema (Windows Script Host). O SAC baixava o arquivo inteiro e oferecia um botão "Baixar".
 *
 * A lista é de EXECUTÁVEIS e scripts, não de "tipos suspeitos": nenhum cliente de uma loja de
 * adesivos manda um `.exe` ou um `.bat` por motivo legítimo, então bloquear não custa nada. O que
 * cliente manda de verdade — PDF, imagem, ZIP com arte, SVG, CDR, AI — fica de fora de propósito:
 * um bloqueio que pega arte de cliente é um bloqueio que alguém desliga.
 *
 * Mora em contracts porque o servidor decide não baixar e a tela decide não oferecer o download
 * — inclusive para arquivos que chegaram antes desta regra existir.
 * ==========================================================================*/
const EXTENSOES_PERIGOSAS = new Set([
    'js', 'jse', 'mjs', 'vbs', 'vbe', 'wsf', 'wsh', 'hta', 'bat', 'cmd', 'ps1', 'psm1', 'sh',
    'exe', 'scr', 'com', 'pif', 'cpl', 'msi', 'msp', 'dll', 'reg', 'lnk', 'jar', 'apk', 'appx',
    'iso', 'img', 'vhd',
]);
const MIMES_PERIGOSOS = [
    'javascript', 'ecmascript', 'vbscript', 'x-msdownload', 'x-msdos-program', 'portable-executable',
    'x-msi', 'x-ms-installer', 'java-archive', 'android.package-archive', 'x-sh', 'x-iso9660',
];
/**
 * Pelo nome OU pelo tipo. Pelo nome, vale a ÚLTIMA extensão: `nota.pdf.js` é um `.js` vestido de
 * PDF, que é exatamente o truque do golpe. Pelo tipo, porque o nome pode vir limpo e o conteúdo
 * não — nos três casos reais o WhatsApp não mandou mimetype, mas o arquivo baixado era
 * `text/javascript`.
 */
export function isDangerousFile(fileName, mime) {
    const ext = (fileName ?? '').trim().toLowerCase().split('.').pop() ?? '';
    if ((fileName ?? '').includes('.') && EXTENSOES_PERIGOSAS.has(ext))
        return true;
    const m = (mime ?? '').toLowerCase();
    return m !== '' && MIMES_PERIGOSOS.some((x) => m.includes(x));
}
/** O mimetype de um data URI — `data:text/javascript;base64,…` → `text/javascript`. */
export function mimeOfDataUrl(url) {
    const m = /^data:([^;,]+)/.exec(url ?? '');
    return m ? m[1].toLowerCase() : null;
}
/* ============================================================================
 * Cordialidade
 *
 * Um aviso antes de enviar, nunca um bloqueio: o atendente sempre pode mandar o original.
 * ==========================================================================*/
export const CORDIALITY_CHOICES = ['suggestion', 'edited', 'original', 'cancelled', 'skipped_error'];
export const cordialityCheckSchema = z.object({ text: z.string().min(1).max(8000) });
export const cordialityQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(3650).optional(),
    channel: channelTypeSchema.optional(),
    attendant: z.string().uuid().optional(),
    choice: z.enum([...CORDIALITY_CHOICES, 'pending']).optional(),
    limit: z.coerce.number().int().min(1).max(2000).optional().default(100),
    offset: z.coerce.number().int().min(0).optional().default(0),
});
/* ============================================================================
 * Links: quais domínios podem sair em cada canal
 *
 * Por ESCOPO, e não por canal: a Shopee antes e depois da compra são o mesmo canal técnico, mas
 * regras diferentes. `allow` = só os domínios da lista; `block` = tudo, menos os da lista.
 * ==========================================================================*/
export const LINK_SCOPES = [
    { id: 'mercadolivre_question', label: 'Mercado Livre — Perguntas (antes da compra)' },
    { id: 'mercadolivre', label: 'Mercado Livre — Mensagens (pós-venda)' },
    { id: 'shopee_pre', label: 'Shopee — chat antes da compra' },
    { id: 'shopee_post', label: 'Shopee — chat após a compra' },
    { id: 'amazon', label: 'Amazon' },
    { id: 'tiktok', label: 'TikTok — atendimento' },
    { id: 'tiktok_comment', label: 'TikTok — comentários' },
    { id: 'whatsapp', label: 'WhatsApp' },
    { id: 'instagram', label: 'Instagram' },
    { id: 'site', label: 'Chat do site' },
    { id: 'email', label: 'E-mail' },
];
export const LINK_SCOPE_IDS = LINK_SCOPES.map((s) => s.id);
export const linkScopeLabel = (id) => LINK_SCOPES.find((s) => s.id === id)?.label ?? id;
/** Domínio: letras, números, hífen e pontos, com pelo menos um ponto. Sem esquema nem caminho. */
export const linkDomainSchema = z
    .string()
    .trim()
    .toLowerCase()
    .transform((d) => d.replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, ''))
    .pipe(z.string().regex(/^[a-z0-9-]+(\.[a-z0-9-]+)+$/, 'domínio inválido'));
export const saveLinkPolicySchema = z.object({
    mode: z.enum(['allow', 'block']),
    domains: z.array(linkDomainSchema).max(200),
});
export const linkCheckSchema = z.object({ text: z.string().max(8000) });
export const linkRequestSchema = z.object({
    conversationId: z.string().uuid(),
    domains: z.array(linkDomainSchema).min(1).max(10),
    /** O trecho com o link, para o gestor ver o contexto antes de liberar. */
    sample: z.string().max(500).optional(),
});
//# sourceMappingURL=index.js.map